import { Duration } from 'luxon'
import { isEqual, orderBy, uniqBy } from 'lodash'

import { ComputedRef } from 'vue'
import { AnalyticsRequest } from './types'
import { AnalyticsField, Asset, AssetClass, DropdownListData, Filter, FilterComparison, FilterParam, RepositoryTimeline, TagClass } from '@types'
import { AssetsBunchStore } from '@/store/assets/utils/types'
import { TagsBunchStore } from '@/store/tags/utils/types'

import { ASSET_FIELD, TAG_FIELD, UNKNOWN_VALUE } from '@/const'
import { TABLE_NAME as ASSETS_TABLE_NAME } from '@/store/assets'
import { TABLE_NAME as TAGS_TABLE_NAME } from '@/store/tags'
import { COMPARED_FIELDS } from './const'

import { DuckDBService } from '@/services/duckdb'

export const prepareDatesRangeSlice = (dates: string[], periods?: number) => {
  if (!dates.length || !periods) return []

  if (dates.length > periods) {
    dates = dates.slice(-periods)
  }
  return dates
}

export const convertToSeconds = (millis: number) =>
  Math.ceil(Duration.fromMillis(millis).as('seconds'))

export const compareRequestWithResponse = (
  request: AnalyticsRequest | undefined,
  repositoryId: string | undefined,
  timeline: RepositoryTimeline,
  rootId: string | undefined,
) => {
  if (!request || !repositoryId || !rootId) return false
  const compareRepositoryId = request.rid === repositoryId
  const compareTimelines = isEqual(request.timeline, timeline)
  const compareParams =
    (request.data === 'position' &&
      request.params.accounts === JSON.stringify([rootId])) ||
    request.params.root === rootId
  return compareRepositoryId && compareTimelines && compareParams
}

export const mapTags = (
  tags: string[],
  tagsBunch: Map<string, TagClass>,
  tagsList?: string[],
): Record<string, string> => {
  const result = Object.fromEntries(
    tagsList?.map(tag => [tag, UNKNOWN_VALUE]) || [],
  )
  tags.forEach(tagId => {
    const tag = tagsBunch.get(tagId)
    if (tag) {
      result[tag.field<string>(TAG_FIELD.TAG_NAME).value] = tag.field<string>(
        TAG_FIELD.TAG_VALUE,
      ).value
    }
  })
  return result
}

export const prepareAsset = <T extends { asset_id: string; account_id: string; tags: string[] }>(
  item: T,
  assetsBunch: Map<string, AssetClass>,
  tagsBunch: Map<string, TagClass>,
  tagsList?: string[],
) => {
  const asset = assetsBunch.get(item.asset_id)
  const asset_id = asset?.field<string>(ASSET_FIELD.NAME).value || ''
  const asset_type = asset?.field<string>(ASSET_FIELD.TYPE).value || ''
  const account = assetsBunch.get(item.account_id)
  const account_id = account?.field<string>(ASSET_FIELD.NAME).value || ''
  const account_type = account?.field<string>(ASSET_FIELD.TYPE).value || ''
  const tags = mapTags(item.tags, tagsBunch)
  return {
    ...item,
    [AnalyticsField.ASSET_ID]: asset_id,
    [AnalyticsField.ASSET_TYPE]: asset_type,
    [AnalyticsField.ACCOUNT_ID]: account_id,
    [AnalyticsField.ACCOUNT_TYPE]: account_type,
    ...(tagsList ? Object.fromEntries(tagsList.map(tag => [tag, UNKNOWN_VALUE])) : {}),
    ...tags,
  }
}

export const getFilterValues = <T extends Record<string, any>>(
  tableName: string,
  date: ComputedRef<string | undefined>,
  assetsBunchStore: AssetsBunchStore,
  dateField: string = 'date',
) => {
  const duckdbInstance = DuckDBService.instance()
  return async (
    name: string,
    field: keyof Asset,
    getter = (_: string, label: string) => ({ value: label, label })
  ) => {
    const selectedDate = date.value
    if (!selectedDate) return []
    const result = await duckdbInstance.query<T>(
      `SELECT DISTINCT ${name} FROM ${tableName} WHERE ${dateField} = '${selectedDate}'`,
    )
    const values = result?.map((value: T) => {
      const assetId = value[name] as string
      const asset = assetsBunchStore.getElementById(assetId)
      const label = asset?.field<string>(field).value || UNKNOWN_VALUE
      return getter(assetId, label)
    })
    const orderedValues = orderBy(uniqBy(values, 'value'), 'label')
    return orderedValues
  }
}

export const getFilterTags = (
  tableName: string,
  date: ComputedRef<string | undefined>,
  tagsBunchStore: TagsBunchStore,
  dateField: string = 'date',
) => {
  const duckdbInstance = DuckDBService.instance()
  return async (field: string) => {
    const selectedDate = date.value
    if (!selectedDate) return []
    const result = await duckdbInstance.query<{ tag: string }>(
      `SELECT DISTINCT(unnest(tags)) as tag FROM ${tableName} WHERE ${dateField} = '${selectedDate}'`,
    )
    const tagIds: string[] = []
    const list = result?.reduce((acc, item) => {
      const tagId = item.tag
      const tag = tagsBunchStore.getElementById(tagId)
      const tagName = tag?.field<string>(TAG_FIELD.TAG_NAME).value
      if (tagName === field) {
        const tagValue = tag?.field<string>(TAG_FIELD.TAG_VALUE).value || UNKNOWN_VALUE
        tagValue && acc.push({ value: tagValue, label: tagValue })
        tagIds.push(tagId)
      }
      return acc
    }, [] as DropdownListData[]) || []
    if (tagIds.length) {
      const escapedTagIds = tagIds.map(tagId => tagId.replace(/'/g, '\'\''))
      const arrayLiteral = escapedTagIds.map(id => `'${id}'`).join(',')
      const resultWithoutValue = await duckdbInstance.queryOne<{ count: number }>(
        `SELECT count(*) as count FROM ${tableName} WHERE ${dateField} = '${selectedDate}' AND (tags IS NULL OR NOT (tags && ARRAY[${arrayLiteral}]))`,
      )
      const count = parseInt(resultWithoutValue?.count?.toString() || '0')
      if (count) {
        list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
      }
    }
    if (!list?.length) {
      list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
    }
    return orderBy(list, 'label')
  }
}


// duckdb helpers

// choose account or asset table prefix based on field key
const getAnalyticsDuckDbPrefix = (
  key: string,
  isTag = false,
  accountPrefix = 'act.',
  assetPrefix = 'ast.',
  tagPrefix = 'avt.',
  valuePrefix = 'tt.'
) => isTag ? tagPrefix : key.startsWith('account_') ? accountPrefix : key.startsWith('asset_') ? assetPrefix : valuePrefix

const isAnalyticsTagField = (field: string, tagNames?: string[]) => tagNames?.includes(field)

export const getAnalyticsDuckDbFieldName = (tagNames?: string[]) => (field: string) => {
  return isAnalyticsTagField(field, tagNames) ? `"${field}"` : field
}

const getFieldNameWithPrefix = (tagNames?: string[]) => (field: string) => {
  const fieldName = getAnalyticsDuckDbFieldName(tagNames)
  const isTag = isAnalyticsTagField(field, tagNames)
  return `${getAnalyticsDuckDbPrefix(field, isTag, 'tt.', 'tt.', '', 'tt.')}${fieldName(field)}`
}

const escapeSqlString = (value: string): string => value.replace(/'/g, '\'\'')
const isNumericLike = (val: unknown) => {
  if (val === null || val === undefined) return false
  if (Array.isArray(val)) return false
  return !Number.isNaN(Number(val))
}
const formatValue = (val: unknown) => {
  if (isNumericLike(val)) {
    return String(val)
  }
  const str = String(val ?? '')
  return `'${escapeSqlString(str)}'`
}

// prepare filter conditions for pivot table
const getParamString = (prepareName: ReturnType<typeof getFieldNameWithPrefix>) =>(param: FilterParam) => {
  const fieldName = prepareName(param.field || '')
  const fieldValue = formatValue(param.value)
  switch (param.comparison) {
    case FilterComparison.EQ:
      return `${fieldName} = ${fieldValue}`
    case FilterComparison.NOTEQ:
      return `${fieldName} <> ${fieldValue}`
    case FilterComparison.CONTAINS:
      return `${fieldName} LIKE '%' || ${fieldValue} || '%'`
    case FilterComparison.NOTCONTAINS:
      return `${fieldName} NOT LIKE '%' || ${fieldValue} || '%'`
    case FilterComparison.AFTER:
      return `${fieldName} > ${fieldValue}`
    case FilterComparison.BEFORE:
      return `${fieldName} < ${fieldValue}`
    case FilterComparison.LAST:
      return `${fieldName} = ${fieldValue}`
    case FilterComparison.RANGE:
      if (Array.isArray(param.value) && param.value.length === 2) {
        const start = String(param.value[0] ?? '')
        const end = String(param.value[1] ?? '')
        const escapedStart = escapeSqlString(start)
        const escapedEnd = escapeSqlString(end)
        return `${fieldName} BETWEEN '${escapedStart}' AND '${escapedEnd}'`
      }
      return `${fieldName} BETWEEN ${formatValue(param.value)} AND ${formatValue(param.value)}`
    case FilterComparison.GREATER:
      return `${fieldName} > ${fieldValue}`
    case FilterComparison.LESS:
      return `${fieldName} < ${fieldValue}`
    case FilterComparison.EMPTY:
      return `${fieldName} IS NULL`
    case FilterComparison.NOTEMPTY:
      return `${fieldName} IS NOT NULL`
  }
}

const mapAnalyticsDuckDbFilter = (filter: Filter | undefined, prepareName: ReturnType<typeof getFieldNameWithPrefix>) => {
  if (!filter) return
  const result = filter.params.map(param => {
    if (!param.params) {
      return getParamString(prepareName)(param)
    }
    const innerResult = param.params.map(getParamString(prepareName))
    return `(${innerResult.join(` ${param.logic} `)})`
  })
  return result.join(` ${filter.logic} `)
}

export const getAnalyticsTotalQuerySelect = (tableName: string, staticFilterConditions?: Record<string, string>, posibleValues: string[] = [], splitBySign = false) => {
  const result = []
  // build totals CTE: join assets/accounts and aggregate raw values
  result.push('WITH totals AS (')
  result.push('\tSELECT')
  const selectConditions: string[] = []
  const groupBy: string[] = []
  selectConditions.push(`\t\tavt.${AnalyticsField.DATE} AS ${AnalyticsField.DATE}`)
  for (const [key, value] of Object.entries(COMPARED_FIELDS)) {
    selectConditions.push(`\t\t${getAnalyticsDuckDbPrefix(key)}${value} AS ${key}`)
  }
  selectConditions.push('\t\tavt.tags')
  for (const value of posibleValues) {
    selectConditions.push(`\t\tSUM(${value}) AS ${value}`)
  }
  if (splitBySign) {
    for (const value of posibleValues) {
      selectConditions.push(`\t\t(CASE WHEN ${value} > 0 THEN 1 ELSE 0 END) AS ${value}_positive`)
      groupBy.push(`${value}_positive`)
    }
  }
  result.push(selectConditions.join(',\n'))
  result.push(`\tFROM ${tableName} avt`)
  result.push(`\tLEFT JOIN ${ASSETS_TABLE_NAME} act ON act.${ASSET_FIELD.ID} = avt.${AnalyticsField.ACCOUNT_ID}`)
  result.push(`\tLEFT JOIN ${ASSETS_TABLE_NAME} ast ON ast.${ASSET_FIELD.ID} = avt.${AnalyticsField.ASSET_ID}`)
  // apply optional static filters
  const staticFilterConditionEntries = staticFilterConditions && Object.entries(staticFilterConditions)
  if (staticFilterConditionEntries?.length) {
    const filterConditions = staticFilterConditionEntries.map(([key, value]) => `${key} = '${value}'`)
    result.push(`\tWHERE ${filterConditions.join(' AND ')}`)
  }
  // group by account/asset fields and raw tag array
  groupBy.push(`avt.${AnalyticsField.DATE}`)
  for (const key of Object.keys(COMPARED_FIELDS)) {
    groupBy.push(`${getAnalyticsDuckDbPrefix(key)}${COMPARED_FIELDS[key as keyof typeof COMPARED_FIELDS]}`)
  }
  groupBy.push('avt.tags')
  result.push(`\tGROUP BY ${groupBy.join(', ')}`)
  result.push(')')
  return result
}

export const getAnalyticsBaseQuerySelect = (filter?: Filter, tagNames?: string[], posibleValues: string[] = [], preSetFilterConditions: string[] = [], splitBySign = false) => {
  const result = []
  // build base CTE: pivot tag names into columns and keep value aggregates
  result.push('base AS (')
  result.push('\tSELECT')
  const selectConditions: string[] = []
  const groupBy: string[] = []
  selectConditions.push(`\t\ttt.${AnalyticsField.DATE}`)
  for (const key of Object.keys(COMPARED_FIELDS)) {
    selectConditions.push(`\t\ttt.${key}`)
  }
  // keep aggregated values from totals
  for (const value of posibleValues) {
    selectConditions.push(`\t\ttt.${value}`)
    groupBy.push(`tt.${value}`)
    if (splitBySign) {
      selectConditions.push(`\t\ttt.${value}_positive`)
      groupBy.push(`tt.${value}_positive`)
    }
  }
  // add tag columns (one per tag name) with UNKNOWN fallback
  for (const row of tagNames || []) {
    selectConditions.push(`\t\tCOALESCE(MAX(CASE WHEN tg.${TAG_FIELD.TAG_NAME} = '${row}' THEN tg.${TAG_FIELD.TAG_VALUE} END), '${UNKNOWN_VALUE}') AS "${row}"`)
  }
  result.push(selectConditions.join(',\n'))
  result.push('\tFROM totals as tt')
  // join tags only when tag columns are requested
  if (tagNames?.length) {
    result.push('\tLEFT JOIN UNNEST(tt.tags) AS u(tag_id) ON TRUE')
    result.push(`\tLEFT JOIN ${TAGS_TABLE_NAME} tg ON tg.${TAG_FIELD.ID} = u.tag_id AND tg.${TAG_FIELD.TAG_NAME} IN (${tagNames?.map(row => `'${row}'`).join(', ')})`)
  }
  // group by all non-aggregated columns in base
  groupBy.push(`tt.${AnalyticsField.DATE}`)
  for (const key of Object.keys(COMPARED_FIELDS)) {
    groupBy.push(`tt.${key}`)
  }
  result.push(`\tGROUP BY ${groupBy.join(', ')}`)
  // apply dynamic filters
  const filterConditions: string[] = preSetFilterConditions
  const dynamicConditions = mapAnalyticsDuckDbFilter(filter, getFieldNameWithPrefix(tagNames))
  if (dynamicConditions) {
    filterConditions.push(dynamicConditions)
  }
  // apply filters
  if (filterConditions.length) {
    result.push(`\tHAVING ${filterConditions.join(' AND ')}`)
  }
  result.push(')')
  return result
}

export const getAnalyticsSummarySelect = (amountField: string) => {
  const result = []
  result.push('summary AS (')
  result.push('\tSELECT')
  result.push('\t\tCOUNT(*) AS total,')
  result.push(`\t\tSUM(${amountField}) AS sum,`)
  result.push(
    `\t\tSUM(CASE WHEN ${amountField} > 0 THEN ${amountField} ELSE 0 END) AS positive_sum,`,
  )
  result.push(
    `\t\tSUM(CASE WHEN ${amountField} < 0 THEN ${amountField} ELSE 0 END) AS negative_sum,`,
  )
  result.push('\tFROM result')
  result.push(')')
  return result
}
